// Client-side audio splitter: decodes a recorded blob, resamples to 16 kHz mono
// (Whisper's native rate — no quality loss) and slices it into WAV chunks so they
// can be transcribed in parallel for a big speed-up on long consultations.

async function decodeToMono16k(blob) {
  const arrayBuf = await blob.arrayBuffer();
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) throw new Error("WebAudio unsupported");
  const ctx = new AC();
  let decoded;
  try {
    decoded = await ctx.decodeAudioData(arrayBuf);
  } finally {
    if (ctx.close) ctx.close();
  }
  const targetRate = 16000;
  const frames = Math.ceil(decoded.duration * targetRate);
  const OAC = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  const offline = new OAC(1, frames, targetRate);
  const src = offline.createBufferSource();
  src.buffer = decoded;
  src.connect(offline.destination);
  src.start(0);
  const rendered = await offline.startRendering();
  return { data: rendered.getChannelData(0), rate: targetRate };
}

function floatToWav(float32, sampleRate) {
  const numSamples = float32.length;
  const buffer = new ArrayBuffer(44 + numSamples * 2);
  const view = new DataView(buffer);
  const writeStr = (off, s) => { for (let i = 0; i < s.length; i++) view.setUint8(off + i, s.charCodeAt(i)); };
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + numSamples * 2, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);   // PCM
  view.setUint16(22, 1, true);   // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);  // 16-bit
  writeStr(36, "data");
  view.setUint32(40, numSamples * 2, true);
  let off = 44;
  for (let i = 0; i < numSamples; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    off += 2;
  }
  return new Blob([view], { type: "audio/wav" });
}

// Returns { chunks: Blob[], duration: seconds }
export async function toWavChunks(blob, chunkSeconds = 25) {
  const { data, rate } = await decodeToMono16k(blob);
  const perChunk = chunkSeconds * rate;
  const chunks = [];
  for (let start = 0; start < data.length; start += perChunk) {
    const slice = data.subarray(start, Math.min(start + perChunk, data.length));
    if (slice.length > 0) chunks.push(floatToWav(slice, rate));
  }
  return { chunks, duration: data.length / rate };
}
